/**
 * Consolidated media cropping utilities
 * Handles coordinate transformations, bounding box calculations, and media frame capture
 * Supports VIDEO, IMAGE, and OBJECT types
 */

type CropCoordinates = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type BoundingBox = [number, number, number, number];

interface VideoCoordinateTransformParams {
  cropCoordinates: { x: number; y: number; width: number; height: number };
  videoElement: HTMLVideoElement;
  svgDimensions: { width: number; height: number };
}

/**
 * Converts crop coordinates to bounding box format [x, y, x2, y2]
 */
export const cropCoordinatesToBoundingBox = (
  cropCoordinates?: CropCoordinates,
): BoundingBox | undefined => {
  if (!cropCoordinates) {
    return undefined;
  }

  return [
    cropCoordinates.x,
    cropCoordinates.y,
    cropCoordinates.x + cropCoordinates.width,
    cropCoordinates.y + cropCoordinates.height,
  ] as BoundingBox;
};

/**
 * Gets the actual video content dimensions (not CSS element dimensions)
 * Calculates based on native aspect ratio and how video fits in element
 */
export const getVideoContentDimensions = (
  videoElement: HTMLVideoElement,
): { width: number; height: number } => {
  const rect = videoElement.getBoundingClientRect();
  const elementWidth = rect.width;
  const elementHeight = rect.height;

  const videoWidth = videoElement.videoWidth;
  const videoHeight = videoElement.videoHeight;

  if (!videoWidth || !videoHeight) {
    console.warn("Video not loaded, using element dimensions");
    return { width: elementWidth, height: elementHeight };
  }

  const videoAspectRatio = videoWidth / videoHeight;
  const elementAspectRatio = elementWidth / elementHeight;

  let contentWidth, contentHeight;

  if (elementAspectRatio > videoAspectRatio) {
    // Element is wider than video - video fits to height, letterboxed on sides
    contentHeight = elementHeight;
    contentWidth = contentHeight * videoAspectRatio;
  } else {
    // Element is taller than video - video fits to width, letterboxed top/bottom
    contentWidth = elementWidth;
    contentHeight = contentWidth / videoAspectRatio;
  }

  return { width: Math.round(contentWidth), height: Math.round(contentHeight) };
};

/**
 * Gets the display dimensions of a video element
 * Returns the bounding rectangle dimensions
 * @deprecated Use getVideoContentDimensions for actual video content size
 */
export const getVideoDisplayDimensions = (
  videoElement: HTMLVideoElement,
): { width: number; height: number } => {
  const rect = videoElement.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
};

/**
 * Transforms crop coordinates from SVG placeholder space to video element space
 * Simple approach: just scale from SVG dimensions to video element dimensions
 */
export const transformCropCoordinatesForVideo = (
  params: VideoCoordinateTransformParams,
): { x: number; y: number; width: number; height: number } => {
  const { cropCoordinates, videoElement, svgDimensions } = params;

  // Get actual video content dimensions (not CSS element size)
  const contentDimensions = getVideoContentDimensions(videoElement);
  const videoRect = videoElement.getBoundingClientRect();

  // Calculate offset for letterboxing/pillarboxing
  const offsetX = (videoRect.width - contentDimensions.width) / 2;
  const offsetY = (videoRect.height - contentDimensions.height) / 2;

  // Simple scaling from SVG space to video content space
  const scaleX = contentDimensions.width / svgDimensions.width;
  const scaleY = contentDimensions.height / svgDimensions.height;

  // Transform coordinates and add letterbox offset
  const transformed = {
    x: cropCoordinates.x * scaleX + offsetX,
    y: cropCoordinates.y * scaleY + offsetY,
    width: cropCoordinates.width * scaleX,
    height: cropCoordinates.height * scaleY,
  };

  // Note: cropCoordinates are already integers from the hook
  return transformed;
};

/**
 * Utility function to capture a video frame and convert it to a File
 * Handles coordinate scaling for cropping operations
 */
export const saveVideoFrameToFile = async (
  videoElement: HTMLVideoElement,
  cropCoordinates?: CropCoordinates,
): Promise<{
  file: File;
  scaledCoordinates?: CropCoordinates;
}> => {
  return new Promise((resolve, reject) => {
    if (!videoElement) {
      console.error("Video element not found");
      reject(new Error("Video element not available"));
      return;
    }

    // Check if video is loaded and has dimensions
    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      console.error("Video not loaded or has no dimensions", {
        readyState: videoElement.readyState,
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
      });
      reject(new Error("Video not loaded properly"));
      return;
    }

    // Check video ready state
    if (videoElement.readyState < 2) {
      // HAVE_CURRENT_DATA
      console.error("Video not ready for capture", {
        readyState: videoElement.readyState,
      });
      reject(new Error("Video not ready for capture"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("Failed to get canvas 2D context");
      reject(new Error("Canvas context not available"));
      return;
    }

    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    // Calculate scaling if crop coordinates provided
    let scaledCoordinates:
      | { x: number; y: number; width: number; height: number }
      | undefined;
    if (cropCoordinates) {
      const actualVideoWidth = videoElement.videoWidth;
      const actualVideoHeight = videoElement.videoHeight;
      const contentDimensions = getVideoContentDimensions(videoElement);

      // Scale from content display size to native video resolution
      const scaleX = actualVideoWidth / contentDimensions.width;
      const scaleY = actualVideoHeight / contentDimensions.height;

      // cropCoordinates already include letterbox offset from transformCropCoordinatesForVideo
      // We need to remove the letterbox offset first, then scale to native resolution
      const videoRect = videoElement.getBoundingClientRect();
      const offsetX = (videoRect.width - contentDimensions.width) / 2;
      const offsetY = (videoRect.height - contentDimensions.height) / 2;

      // Remove letterbox offset to get pure content coordinates
      const contentCoords = {
        x: cropCoordinates.x - offsetX,
        y: cropCoordinates.y - offsetY,
        width: cropCoordinates.width,
        height: cropCoordinates.height,
      };

      // Note: cropCoordinates are already integers from the hook
      scaledCoordinates = {
        x: contentCoords.x * scaleX,
        y: contentCoords.y * scaleY,
        width: contentCoords.width * scaleX,
        height: contentCoords.height * scaleY,
      };
    }

    canvas.toBlob(
      (blob: any) => {
        const file = new File([blob], "video_frame", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        resolve({ file, scaledCoordinates });
      },
      "image/jpeg",
      1,
    );
  });
};
